<template>
  <div class="boxing-results-container">
    <div class="results-card">
      <!-- Results Header -->
      <div class="results-header">
        <h3 class="results-title">Boxing Results</h3>
        <span class="fight-count">{{ matches.length }} {{ matches.length === 1 ? 'fight' : 'fights' }}</span>
      </div>

      <!-- Boxing Matches -->
      <div class="boxing-list">
        <div
          class="boxing-match"
          v-for="match in matches"
          :key="match.id || match._id"
        >
          <div class="fighters-section">
            <span class="fighter-name">{{ getFighterName(match.team1) }}</span>
            <span class="vs-divider">vs</span>
            <span class="fighter-name">{{ getFighterName(match.team2) }}</span>
          </div>

          <div class="match-result">
            {{ getResultText(match) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BoxingResults',
  props: {
    matches: {
      type: Array,
      required: true,
      default: () => []
    },
    group: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  methods: {
    getFighterName(team) {
      if (!team) return 'Unknown Fighter';

      // Try different possible name fields
      if (team.name && team.name.trim()) return team.name;
      if (team.member?.profile?.firstName && team.member?.profile?.lastName) {
        return `${team.member.profile.firstName} ${team.member.profile.lastName}`;
      }
      if (team.member?.name) return team.member.name;
      if (team.fighter) return team.fighter;

      return 'Unknown Fighter';
    },

    getTotalRounds() {
      return this.group?.boxingConfig?.rounds ||
             this.group?.rounds ||
             10;
    },

    getResultText(match) {
      // Try to get result from different possible locations
      const result = match.result || match.boxingResult?.fightResult;

      if (!result) return 'No Result';

      const method = result.method;
      const round = result.round;

      if (method && round) {
        return `${this.formatMethod(method)} - Round ${round}`;
      } else if (method) {
        return `${this.formatMethod(method)} - Round ${this.getTotalRounds()}`;
      }

      return 'Match Result';
    },

    formatMethod(method) {
      if (!method) return 'Decision';

      const methodMap = {
        'tko': 'Technical KO',
        'ko': 'KO',
        'decision': 'Points',
        'submission': 'Submission',
        'disqualification': 'DQ',
        'no-contest': 'No Contest'
      };
      return methodMap[method.toLowerCase()] || method;
    },

    formatDate(date) {
      if (!date) return '';

      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';

        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
      } catch (error) {
        return '';
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.boxing-results-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem;
}

.results-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  .results-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: #111827;
  }

  .fight-count {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 400;
  }
}

.boxing-list {
  display: flex;
  flex-direction: column;
}

.boxing-match {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
}

.fighters-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;

  .fighter-name {
    color: #111827;
    font-weight: 500;
    font-size: 0.9rem;
  }

  .vs-divider {
    font-size: 0.8rem;
    font-weight: 400;
    color: #6b7280;
    padding: 0 0.25rem;
  }
}

.match-result {
  color: #374151;
  font-size: 0.85rem;
  font-weight: 500;
  text-align: right;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .boxing-results-container {
    padding: 0.25rem;
  }

  .results-header {
    padding: 0.75rem 1rem;

    .results-title {
      font-size: 1rem;
    }

    .fight-count {
      font-size: 0.7rem;
    }
  }

  .boxing-match {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 1rem;

    .fighters-section {
      width: 100%;

      .fighter-name {
        font-size: 0.85rem;
      }

      .vs-divider {
        font-size: 0.75rem;
      }
    }

    .match-result {
      text-align: left;
      font-size: 0.8rem;
      color: #6b7280;
    }
  }
}
</style>